/**
 * useSmartBack ✦ — iter196 Universal Back Navigation Contract
 * ─────────────────────────────────────────────────────────────
 * The single source of truth for "Back ✦" behaviour across every
 * guest surface (Booth OS, Memory Wall, Lens Notes, Drop One Word,
 * Memory Snap, Flip Guestbook, Audio/Video Guestbook, Pocket Booth,
 * Spin Film, Film, Highlight Reel, RSVP, Access).
 *
 * Resolution priority (locked):
 *
 *    1. Real SPA history       — when we know the previous page
 *                                was inside this app, navigate(-1).
 *    2. entry='booth_os'       — return to /booth-os/:event_id.
 *    3. guest_id 'access_*'    — return to /access/:slug.
 *    4. guest_id (personal)    — return to /event/:slug/g/:guest_id.
 *    5. fallbackHref           — page-specific override (rare).
 *    6. /event/:slug           — host event page (last resort).
 *
 * The hook NEVER redirects to '/' or to the lookup screen unless
 * the guest truly has no session AND no slug context. The whole
 * point is that a guest can't be "kicked back to login" by an
 * accidental tap.
 *
 * Usage:
 *
 *    const { goBack, target } = useSmartBack({ fallbackHref });
 *    <button onClick={goBack}>Back ✦</button>
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadGuestSession } from '../guestSession';

const SPA_NAV_FLAG = 'eventdrop_spa_navigated';

const extractSlug = () => {
  if (typeof window === 'undefined') return null;
  const path = window.location.pathname;
  let m;
  if ((m = path.match(/^\/event\/([^/]+)/))) return m[1];
  if ((m = path.match(/^\/lens-note\/([^/]+)/))) return m[1];
  if ((m = path.match(/^\/one-word\/([^/]+)/))) return m[1];
  if ((m = path.match(/^\/film\/([^/]+)/))) return m[1];
  if ((m = path.match(/^\/access\/([^/]+)/))) return m[1];
  return null;
};

const hasRealSPAHistory = () => {
  if (typeof window === 'undefined') return false;
  return window.history.length > 1 &&
    window.sessionStorage.getItem(SPA_NAV_FLAG) === '1';
};

export function resolveSmartBackTarget({ slug, fallbackHref } = {}) {
  // Resolve the canonical fallback href when SPA history is empty.
  // Slug auto-detected from window if not provided (most callers).
  const detectedSlug = slug || extractSlug();
  if (!detectedSlug) return fallbackHref || '/';
  try {
    const session = loadGuestSession(detectedSlug);
    if (session && session.guest_id) {
      // 1. Anchored to Booth OS → return there.
      if (session.entry === 'booth_os' && session.event_id) {
        return `/booth-os/${session.event_id}`;
      }
      // 2. Came in via the access doorway.
      if (String(session.guest_id).startsWith('access_')) {
        return `/access/${detectedSlug}`;
      }
      // 3. Personal-link guest dashboard.
      return `/event/${detectedSlug}/g/${session.guest_id}`;
    }
  } catch { /* fall through */ }
  return fallbackHref || `/event/${detectedSlug}`;
}

export function useSmartBack({ fallbackHref, slug } = {}) {
  const navigate = useNavigate();
  const target = resolveSmartBackTarget({ slug, fallbackHref });
  const goBack = useCallback(() => {
    if (hasRealSPAHistory()) {
      navigate(-1);
    } else {
      navigate(target);
    }
  }, [navigate, target]);
  return { goBack, target };
}

export default useSmartBack;
